<template>
    <div id="character-sheet">
        <top-bar :page="'characterSheet'" v-if="character" />
        <div id="col1">
            <div class="row" v-if="character">
                <div class="col-sm-12 mb-1">
                    <div class="avatar-and-details">
                        <div class="avatar-wrapper">
                            <img
                                :src="character.avatar"
                                :alt="character.name"
                                class="img-thumbnail square-img"
                            />
                            <label
                                for="characterAvatarInput"
                                class="avatar-edit-button"
                                title="Change avatar"
                                aria-label="Change avatar"
                            >
                                ✎
                            </label>
                            <input
                                id="characterAvatarInput"
                                class="visually-hidden"
                                type="file"
                                accept="image/*"
                                @change="changeAvatar"
                            />
                        </div>
                        <div class="avatar-details">
                            <h4 v-if="character.background.background">
                                {{ character.background.background }}
                            </h4>
                            <p v-if="character.background.description">
                                {{ character.background.description }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 mb-3">
                    <div class="input-group mb-3">
                        <span class="input-group-text">Name</span>
                        <input
                            type="text"
                            class="form-control"
                            v-model="character.name"
                        />
                    </div>

                    <div class="col-sm-12 mb-3">
                        <div class="input-group mb-3">
                            <span class="input-group-text">Age</span>
                            <input
                                type="text"
                                class="form-control"
                                v-model="character.age"
                            />
                        </div>
                    </div>

                    <div class="col-sm-12 mb-3">
                        <div class="input-group mb-3">
                            <span class="input-group-text">Level</span>
                            <select
                                class="form-control"
                                v-model="character.level"
                            >
                                <option
                                    v-for="level in 15"
                                    :key="level"
                                    :value="level"
                                >
                                    {{ level }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-sm-12">
                        <div class="input-group mb-3">
                            <span class="input-group-text"
                                >Challenge Rating</span
                            >
                            <select class="form-control" v-model="character.cr">
                                <option v-for="cr in 5" :key="cr" :value="cr">
                                    {{ cr }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Archetype</span>
                        <input
                            type="text"
                            class="form-control"
                            :value="character.archetype"
                            readonly
                        />
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Background</span>
                        <input
                            type="text"
                            class="form-control"
                            :value="character.background.background"
                            readonly
                        />
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Fear</span>
                        <input
                            type="text"
                            class="form-control"
                            v-model="character.fear"
                        />
                    </div>
                    <div class="input-group mb-3">
                        <span class="input-group-text">Speed</span>
                        <select class="form-control" v-model="character.speed">
                            <option value="Slow - 20ft / 4 Hexes">
                                Slow - 20ft / 4 Hexes
                            </option>
                            <option value="Average - 30ft / 6 Hexes">
                                Average - 30ft / 6 Hexes
                            </option>
                            <option value="Fast - 40ft / 8 Hexes">
                                Fast - 40ft / 8 Hexes
                            </option>
                        </select>
                    </div>
                    <div class="input-group mb-3">
                        <span class="input-group-text">Fear Status</span>
                        <select
                            class="form-control"
                            v-model="character.fearStatus"
                        >
                            <option value="Stable">Stable</option>
                            <option value="Afraid">Afraid</option>
                            <option value="Terrified">Terrified</option>
                        </select>
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Status Effect</span>
                        <select
                            class="form-control"
                            :value="selectedStatusEffect"
                            @change="onStatusChange"
                        >
                            <option disabled value="">Select a status...</option>
                            <option
                                v-for="(desc, name) in store.statusEffects"
                                :key="name"
                                :value="name"
                            >
                                {{ name }}
                            </option>
                        </select>
                    </div>

                    <h4>Backstory</h4>
                    <textarea
                        v-model="character.backstory"
                        class="form-control text-start"
                        rows="7"
                    ></textarea>
                </div>
            </div>
        </div>

        <div id="col2">
            <StatsDisplay
                v-if="character"
                v-model:stats="character.stats"
                :diceRoller="true"
                :fearStatus="character.fearStatus"
                :theme="theme"
                @roll-dice="openDiceBox"
            />

            <!-- Active Status Effects -->
            <div
                v-if="
                    character &&
                    character.statusEffects &&
                    character.statusEffects.length
                "
                class="mt-2"
            >
                <div
                    v-for="(se, idx) in character.statusEffects"
                    :key="se.name + '-' + idx"
                    class="alert alert-danger alert-dismissible fade show"
                    role="alert"
                >
                    <strong>{{ se.name }}</strong> — {{ se.effect }}
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        @click.prevent="confirmRemoveStatusEffect(idx, se)"
                    ></button>
                </div>
            </div>

            <div class="row mt-3">
                <div class="col-sm-9">
                    <LifelineTracker
                        v-if="character"
                        :initial="character.lifeline"
                        :hp="16"
                        @update="character.lifeline = $event"
                    />
                </div>
                <div class="col-sm-3">
                    <LuckbankTracker
                        v-if="character"
                        :initial="character.luckbank"
                        :archetype="character.archetype"
                        @updateLuckbank="character.luckbank = $event"
                    />
                </div>
            </div>

            <div class="row">
                <div class="col-sm-6">
                    <WeaponsDisplay
                        v-if="character"
                        :character="character"
                        :fearStatus="character.fearStatus"
                        :stats="character.stats"
                        @roll-dice="openDiceBox"
                    />
                    <ArmourDisplay v-if="character" :character="character" @roll-dice="openDiceBox" />

                    <item-display v-if="character" :character="character" />
                </div>

                <div class="col-sm-6">
                    <AbilityDisplay
                        v-if="character"
                        :abilities="character.abilities"
                        :characterId="character.id"
                        :theme="theme"
                    />
                    <FlawsDisplay
                        v-if="character"
                        :flaws="character.background.flaws"
                        :characterId="character.id"
                        :theme="theme"
                    />
                    <MutagenDisplay
                        v-if="
                            character &&
                            (Object.keys(store.mutagens).length > 0 ||
                                Object.keys(character.mutagens || {}).length > 0)
                        "
                        :character="character"
                    />
                </div>

                <!-- Inventory -->
                <div class="col-sm-6 mt-3" v-if="character">
                    <div class="col-sm-12 mb-3">
                        <div class="input-group mb-3 flex-column">
                            <span class="input-group-text">Other Items</span>
                            <textarea
                                class="form-control"
                                v-model="character.inventory"
                                rows="10"
                            ></textarea>
                        </div>
                    </div>
                </div>

                <!-- Notes -->
                <div class="col-sm-6 mt-3" v-if="character">
                    <div class="col-sm-12 mb-3">
                        <div class="input-group mb-3 flex-column">
                            <span class="input-group-text">Notes</span>
                            <textarea
                                class="form-control"
                                v-model="character.notes"
                                rows="10"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <DoomclockTracker
            v-if="character"
            :initial="character.doomclock"
            @update="character.doomclock = $event"
        />
    </div>

    <dice-box
        v-if="character && diceRoller"
        ref="diceBox"
        :theme="theme"
        :clickedSkillKey="clickedSkillKey"
    />
</template>

<script>
import TopBar from "./TopBar.vue";
import Lifeline from "./Lifeline.vue";
import Doomclock from "./Doomclock.vue";
import Luckbank from "./Luckbank.vue";
import Stats from "./Stats.vue";
import { useRosterStore } from "../stores/index";
import AbilityDisplay from "./AbilityDisplay.vue";
import FlawsDisplay from "./FlawsDisplay.vue";
import ArmourDisplay from "./ArmourDisplay.vue";
import WeaponsDisplay from "./WeaponsDisplay.vue";
import DiceBox from "./DiceBox.vue";
import MutagenDisplay from "./MutagenDisplay.vue";
import ItemDisplay from "./ItemDisplay.vue";
import { resizeImage } from "../utils/resizeImage";

export default {
    components: {
        TopBar,
        LifelineTracker: Lifeline,
        DoomclockTracker: Doomclock,
        StatsDisplay: Stats,
        LuckbankTracker: Luckbank,
        AbilityDisplay,
        FlawsDisplay,
        ArmourDisplay,
        WeaponsDisplay,
        DiceBox,
        MutagenDisplay,
        ItemDisplay,
    },
    setup() {
        const store = useRosterStore();
        return { store };
    },
    data() {
        return {
            character: null,
            diceRoller: true,
            clickedSkillKey: "grit",
            selectedStatusEffect: "",
        };
    },
    props: {
        id: { type: Number, required: true },
    },
    computed: {
        theme() {
            return Number(this.store.theme) || 0;
        },
    },
    methods: {
        async fetchCharacter() {
            const store = useRosterStore();
            try {
                this.character = await store.getCharacterById(this.id);

                // Ensure CR, inventory & notes always exist
                if (this.character) {
                    if (this.character.inventory === undefined) {
                        this.character.inventory = "";
                    }
                    if (this.character.notes === undefined) {
                        this.character.notes = "";
                    }
                    if (this.character.cr === undefined) {
                        this.character.cr = 1;
                    }
                    if (this.character.items === undefined) {
                        this.character.items = [];
                    }
                    if (!Array.isArray(this.character.mutagens)) {
                        this.character.mutagens = [];
                    }
                    if (this.character.statusEffects === undefined) {
                        this.character.statusEffects = [];
                    }
                    if (!Array.isArray(this.character.abilities)) {
                        this.character.abilities = [];
                    }
                } else {
                    console.error("Character not found", {
                        requestedId: this.id,
                        availableCharacters: store.characters,
                    });
                    return;
                }

                this.character.abilities.sort((a, b) => a - b);
            } catch (error) {
                console.error("Failed to fetch character", error);
            }
        },
        async changeAvatar(event) {
            const file = event.target.files[0];
            if (!file || !file.type.startsWith("image/")) return;

            try {
                this.character.avatar = await resizeImage(file);
            } catch (error) {
                console.error("Failed to resize avatar", error);
            } finally {
                event.target.value = "";
            }
        },
        onStatusChange(e) {
            console.log("Status changed:", e.target.value);
            const name = e.target.value;
            if (!name) return;

            // Ensure reactive array exists
            if (!Array.isArray(this.character.statusEffects)) {
                this.character.statusEffects = [];
            }

            // Skip duplicates
            if (this.character.statusEffects.some((se) => se.name === name)) {
                // Reset UI selection and bail
                this.selectedStatusEffect = "";
                e.target.value = "";
                return;
            }

            const effect = this.store.statusEffects?.[name] || "";
            this.character.statusEffects.push({ name, effect });

            // Reset UI selection (both model + DOM to avoid phantom re-emits)
            this.selectedStatusEffect = "";
            e.target.value = "";
        },
        openDiceBox({ skill, talent, statKey }) {
            this.clickedSkillKey = statKey;
            if (!this.diceRoller || !this.$refs.diceBox) return;
            this.$refs.diceBox.showModal(skill, talent);
        },
        confirmRemoveStatusEffect(idx, se) {
            const ok = window.confirm(
                `Remove status effect "${se.name}" from ${this.character.name}?`
            );
            if (!ok) return;

            // Remove from the character in the store (reactive splice)
            this.character.statusEffects.splice(idx, 1);
        },
    },
    created() {
        this.fetchCharacter();
    },
    watch: {
        id: "fetchCharacter",
    },
};
</script>

<style scoped>
/* ===== Desktop / default ===== */
#character-sheet {
    background-color: transparent;
    height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: min-content 1fr 1fr;
    grid-template-areas:
        "nav nav nav"
        "col1 col2 col2"
        "col1 col2 col2";
    color: white;
    grid-gap: 1rem;
    padding: 0;
    overflow-x: hidden;
}

.img-thumbnail {
    max-width: 45%;
    height: auto;
    padding: 0;
}

.avatar-wrapper {
    position: relative;
    display: block;
    width: 45%;
}

.avatar-and-details {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.avatar-details {
    flex: 1;
}

.avatar-wrapper img {
    display: block;
    width: 100%;
    max-width: none;
    border-color: #000;
    background-color: transparent;
}

.avatar-edit-button {
    position: absolute;
    right: 0.35rem;
    bottom: 0.35rem;
    width: 1.9rem;
    height: 1.9rem;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.8);
    border: 1px solid #000;
    color: white;
    cursor: pointer;
    line-height: 1;
    z-index: 1;
}

.avatar-edit-button:hover,
.avatar-edit-button:focus {
    background: rgba(255, 255, 255, 0.9);
    color: #111;
}

.float-left {
    margin-right: 1rem;
    margin-bottom: 1rem;
    float: left;
}

#col1 {
    grid-area: col1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    scrollbar-width: none;
    padding-bottom: 8rem;
}

#col2 {
    grid-area: col2;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    scrollbar-width: none;
    padding-bottom: 8rem;
}

/* Hide scrollbar for WebKit */
#col1::-webkit-scrollbar,
#col2::-webkit-scrollbar {
    display: none;
}

/* ===== Stacked input-group fix ===== */
/* Make label behave like a full-width control */
.input-group.flex-column .input-group-text {
    display: block;
    text-align: left;
    width: 100%;
    border-top-right-radius: 6px !important;
    border-top-left-radius: 6px !important;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom: none; /* avoid double border with textarea */
}

/* Make textarea fill and square to the label */
.input-group.flex-column .form-control {
    width: 100%;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

/* KEY FIX: cancel Bootstrap's horizontal offset (-1px) */
.input-group.flex-column > :not(:first-child) {
    margin-left: 0 !important;
}

/* ===== Mobile overrides ===== */
@media (max-width: 768px) {
    #character-sheet {
        height: auto;
        grid-template-columns: 1fr;
        grid-template-rows: auto auto auto auto;
        grid-auto-rows: auto;
        grid-template-areas:
            "nav"
            "col1"
            "col2"
            "doomClock";
        align-items: start;
        align-content: start;
    }

    #col1,
    #col2 {
        padding: 0.5rem;
        overflow-y: visible;
        padding-bottom: 0 !important;
    }

    .input-group {
        flex-direction: row;
    }

    .input-group .input-group-text {
        width: auto;
        text-align: center;
    }

    .img-thumbnail {
        max-width: 100%;
        height: auto;
    }

    .avatar-wrapper {
        width: 100%;
    }

    .avatar-and-details {
        flex-direction: column;
    }

    #doomClock,
    #doomClock.fixed-bottom {
        position: static !important;
        padding: 0.5rem;
        margin-top: 1rem;
    }
}
</style>

